<template>
    <div :id="id" v-if="privateIsShowModal" class="FormSubmitPopup">
        <div class="MessageWindow">
            <div class="MessageHeader">
                <slot name="header"> {{ privateHeader || $t("lbl_emp2_Message") }} </slot>
            </div>

            <div class="MessageBody">
                <div v-if="isShowProgressSection" class="Progress">
                    <slot name="progress">
                        <div v-if="isShowProgressBarContainer" class="ProgressBarContainer">
                            <div class="ProgressFullBar">
                                <div class="ProgressBar" :style="{ width: progressPercent + '%' }"></div>
                            </div>
                            <div class="ProgressText">{{ progressPercent }}%</div>
                        </div>

                        <div
                            v-if="privateProgress.stepData.steps && privateProgress.stepData.steps.length"
                            class="Steps"
                        >
                            <div v-for="(step, index) in privateProgress.stepData.steps" :key="index" class="Step">
                                <span class="StepStatusIcon">
                                    <img
                                        v-if="isCurrentStepOrPendingStep(step.orderId)"
                                        class="Loading"
                                        :src="circleNotchIconUrl"
                                    />
                                    <img
                                        v-else-if="isStepSuccess(step.orderId)"
                                        class="Success"
                                        :src="circleTickIconUrl"
                                    />
                                    <img
                                        v-else-if="isStepFailed(step.orderId)"
                                        class="Failed"
                                        :src="circleCrossIconUrl"
                                    />
                                </span>
                                <span>{{ step.title }}</span>
                            </div>
                        </div>

                        <div v-if="privateProgress.reminder" class="Reminder">
                            {{ privateProgress.reminder }}
                        </div>
                    </slot>
                </div>

                <div v-if="isShowResultSection" class="Result">
                    <div class="Message">
                        <slot name="message">{{ privateResult.message }}</slot>
                    </div>

                    <slot name="failed">
                        <div v-if="privateResult.failedItems && privateResult.failedItems.length" class="Failed">
                            <div
                                v-for="(failedItem, index) in privateResult.failedItems"
                                :key="index"
                                class="FailedItems"
                            >
                                <div v-if="failedItem.isShowIcon" class="WarnIcon">
                                    <img :src="warnIconUrl" />
                                </div>

                                <div class="DescContainer">
                                    <div class="FailedDesc">
                                        <span class="Desc">{{ failedItem.desc }}</span>
                                    </div>
                                    <div v-if="failedItem.items && failedItem.items.length" class="FailedSteps">
                                        <div v-for="(item, index) in failedItem.items" :key="index" class="FailedStep">
                                            <span class="bull">&bull;</span> {{ item }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </slot>

                    <div class="MessageBtnGroup">
                        <button
                            v-if="privateResult.buttons.cancel.isShow"
                            type="button"
                            @click="HideMessageModal"
                            class="btn btn-outline-light"
                        >
                            {{ privateResult.buttons.cancel.label || $t("lbl_EMPBE_Cancel") }}
                        </button>

                        <button
                            v-if="privateResult.buttons.ok.isShow"
                            type="button"
                            @click="HideMessageModal"
                            class="btn btn-primary"
                        >
                            {{ privateResult.buttons.ok.label || $t("lbl_EMPBE_Confirm") }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="MessageFooter">
                <slot name="footer"> </slot>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
    props: {
        id: {
            type: String,
            default: "FormSubmitPopupMessage",
        },
    },

    data() {
        return {};
    },

    computed: {
        ...mapState({
            privateIsShowModal: (state) => state.submitFormMessage.isShowModal,
            privateStatus: (state) => state.submitFormMessage.status,
            privateHeader: (state) => state.submitFormMessage.header,
            privateProgress: (state) => state.submitFormMessage.progress,
            privateResult: (state) => state.submitFormMessage.result,
        }),
        isShowProgressSection() {
            return this.isShowSection("progress");
        },
        isShowResultSection() {
            return this.isShowSection("result");
        },
        isShowProgressBarContainer() {
            return (
                this.privateProgress.progressBar.isShow &&
                (this.privateProgress.progressBar.value || this.privateProgress.progressBar.value === 0)
            );
        },
        progressPercent() {
            if (this.privateProgress.progressBar.value === 0) return 0;

            const percentageVal = (this.privateProgress.progressBar.value / this.privateProgress.progressBar.total) * 100;

            return Math.round(percentageVal);
        },
        warnIconUrl() {
            return `${process.env.VUE_APP_OSS_ORIGIN}EMP/Backend/svg/PublicTabs/warn.svg`;
        },
        circleTickIconUrl() {
            return `${process.env.VUE_APP_OSS_ORIGIN}EMP/Backend/svg/PublicTabs/CircleTick.svg`;
        },
        circleCrossIconUrl() {
            return `${process.env.VUE_APP_OSS_ORIGIN}EMP/Backend/svg/PublicTabs/CircleCross.svg`;
        },
        circleNotchIconUrl() {
            return `${process.env.VUE_APP_OSS_ORIGIN}EMP/Backend/svg/PublicTabs/CircleNotch.svg`;
        },
    },

    methods: {
        ...mapActions("submitFormMessage", ["HideMessageModal"]),

        isShowSection(section) {
            return this.privateStatus === section;
        },
        isStepFailed(stepOrderId) {
            return this.isCompletedStep(stepOrderId) && this.privateProgress.stepData.failedSteps.includes(stepOrderId);
        },
        isStepSuccess(stepOrderId) {
            return this.isCompletedStep(stepOrderId) && !this.isStepFailed(stepOrderId);
        },
        // Later Steps
        isPendingStep(stepOrderId) {
            return stepOrderId > this.privateProgress.stepData.currentStep;
        },
        // Finished Steps
        isCompletedStep(stepOrderId) {
            return stepOrderId < this.privateProgress.stepData.currentStep;
        },
        // Current Step
        isCurrentStep(stepOrderId) {
            return stepOrderId === this.privateProgress.stepData.currentStep;
        },
        // Next Step
        isNextStep(stepOrderId) {
            return stepOrderId === this.privateProgress.stepData.currentStep + 1;
        },
        // Current Step or Pending Step
        isCurrentStepOrPendingStep(stepOrderId) {
            return this.isCurrentStep(stepOrderId) || this.isPendingStep(stepOrderId);
        },
    },
};
</script>

<style scoped lang="scss">
.FormSubmitPopup {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    padding: 1rem;

    .MessageWindow {
        background-color: #fff;
        width: 100%;
        max-width: 452px;
        border-radius: 0.75rem;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        position: relative;
        padding: 24px;
        font-family: sans-serif;

        .MessageHeader {
            color: rgba(0, 0, 0, 0.8);
            font-size: 18px;
            font-weight: 700;
            line-height: 23.4px;
            word-wrap: break-word;
        }

        .MessageBody {
            .Progress {
                margin-top: 0.75rem;

                .ProgressBarContainer {
                    display: flex;
                    gap: 1rem;
                    align-items: center;

                    .ProgressFullBar {
                        width: 100%;
                        height: 0.5rem;
                        background-color: rgba(0, 0, 0, 0.08);
                        border-radius: 100px;
                        overflow: hidden;

                        .ProgressBar {
                            height: 100%;
                            background-color: #7366ff;
                            transition: width 0.3s ease-in-out;
                        }
                    }

                    .ProgressText {
                        color: rgba(0, 0, 0, 0.6);
                        font-size: 14px;
                        font-weight: 400;
                        line-height: 18.2px;
                        word-wrap: break-word;
                    }
                }

                .Steps {
                    margin-top: 1rem;

                    > .Step {
                        color: rgba(0, 0, 0, 0.8);
                        font-size: 14px;
                        font-weight: 700;
                        line-height: 18.2px;
                        word-wrap: break-word;
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;

                        &:not(:first-child) {
                            margin-top: 1rem;
                        }

                        .Loading {
                            animation: 0.75s linear 0s infinite normal none running loading-spin;
                        }
                    }
                }

                .Reminder {
                    margin-top: 1rem;
                    color: rgba(0, 0, 0, 0.6);
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 18.2px;
                    word-wrap: break-word;
                }
            }

            .Result {
                .Message {
                    margin-top: 1rem;
                    color: rgba(0, 0, 0, 0.8);
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 18.2px;
                    word-wrap: break-word;
                }

                .Failed {
                    margin-top: 1rem;
                    padding: 1rem;
                    color: #cd1515;
                    font-size: 14px;
                    word-wrap: break-word;
                    background: #fcebed;
                    border-radius: 8px;

                    .FailedItems {
                        display: flex;
                        gap: 0.5rem;

                        .WarnIcon {
                        }

                        .DescContainer {
                            .FailedDesc {
                                font-weight: 400;
                                line-height: 20px;
                            }

                            .FailedSteps {
                                margin-top: 0.25rem;

                                > .FailedStep {
                                    font-weight: 700;
                                    line-height: 25.2px;

                                    > span.bull {
                                        margin-inline: 0.25rem;
                                    }
                                }
                            }
                        }
                    }
                }

                .MessageBtnGroup {
                    margin-top: 1rem;
                    display: flex;
                    gap: 0.5rem;
                    justify-content: end;
                    align-items: center;

                    .btn {
                        font-size: 14px;
                        font-weight: 700;
                        line-height: 21px;
                        word-wrap: break-word;

                        &.btn-outline-light {
                            color: rgba(0, 18, 26, 0.6);
                            border-color: #fff;
                        }
                    }
                }
            }
        }

        .MessageFooter {
        }
    }
}

@keyframes loading-spin {
    100% {
        transform: rotate(360deg);
    }
}
</style>
